import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { commonErrorResponses, emptyEnvelopeSchema, requiredPayloadEnvelope, } from '../envelope.js';
import { secretValueSchema, termAcceptanceSchema, tokenRefreshSchema, verificationTimingSchema, } from '../common/schemas.js';
const authUserSchema = z.object({
    id: z.string(),
    email: z.string(),
    name: z.string(),
    role: z.string(),
    hasPassword: z.boolean().optional(),
    totpEnabled: z.boolean().optional(),
    /** TOTP가 켜져 있을 때만 미사용 복구 코드 개수. 재발급 API는 별도. */
    totpRecoveryCodesRemaining: z.number().int().nonnegative().optional(),
});
const authResponseSchema = tokenRefreshSchema.extend({
    user: authUserSchema,
    isTempPassword: z.boolean(),
    hasPassword: z.boolean(),
});
/** TOTP 활성 계정은 1차 인증(이메일 OTP·비밀번호) 성공 시 AT/RT 대신 이 신호만 받는다 —
 * `lunacast_mfa_challenge` 쿠키가 세팅되며, `/auth/login/verify-totp`로 최종 로그인을 완료한다. */
const mfaRequiredResponseSchema = z.object({ mfaRequired: z.literal(true) });
const authOrMfaResponseSchema = z.union([authResponseSchema, mfaRequiredResponseSchema]);
const sessionSchema = z.object({
    familyId: z.string(),
    userAgent: z.string().nullable(),
    createdAt: z.string(),
    lastUsedAt: z.string().nullable(),
});
const loginEventSchema = z.object({
    id: z.string(),
    action: z.string(),
    method: z.string().nullable(),
    userAgent: z.string().nullable(),
    createdAt: z.string(),
});
const c = initContract();
export const userAuthContract = c.router({
    signupStart: {
        method: 'POST',
        path: '/auth/signup/start',
        body: z.object({
            email: z.string().optional(),
            returnTo: z.string().optional(),
            acceptances: z.array(termAcceptanceSchema).optional(),
        }),
        responses: {
            200: requiredPayloadEnvelope(verificationTimingSchema),
            ...commonErrorResponses,
        },
    },
    signupVerify: {
        method: 'POST',
        path: '/auth/signup/verify',
        body: z.object({
            email: z.string().optional(),
            code: z.string().optional(),
            dpopProof: z.string().optional(),
            acceptances: z.array(termAcceptanceSchema).optional(),
            rememberMe: z.boolean().optional(),
        }),
        responses: {
            200: requiredPayloadEnvelope(authOrMfaResponseSchema),
            ...commonErrorResponses,
        },
    },
    loginStart: {
        method: 'POST',
        path: '/auth/login/start',
        body: z.object({
            email: z.string().optional(),
            returnTo: z.string().optional(),
        }),
        responses: {
            200: requiredPayloadEnvelope(verificationTimingSchema),
            ...commonErrorResponses,
        },
    },
    loginVerify: {
        method: 'POST',
        path: '/auth/login/verify',
        body: z.object({
            email: z.string().optional(),
            code: z.string().optional(),
            dpopProof: z.string().optional(),
            rememberMe: z.boolean().optional(),
        }),
        responses: {
            200: requiredPayloadEnvelope(authOrMfaResponseSchema),
            ...commonErrorResponses,
        },
    },
    login: {
        method: 'POST',
        path: '/auth/login',
        body: z.object({
            email: z.string().optional(),
            /** 클라이언트가 서버 enc 공개키로 봉인한 값(JWE Compact). 평문 비밀번호는 계약에 실리지 않는다. */
            password: secretValueSchema.optional(),
            dpopProof: z.string().optional(),
            rememberMe: z.boolean().optional(),
        }),
        responses: {
            200: requiredPayloadEnvelope(authOrMfaResponseSchema),
            ...commonErrorResponses,
        },
    },
    refresh: {
        method: 'POST',
        path: '/auth/refresh',
        body: c.noBody(),
        responses: {
            200: requiredPayloadEnvelope(tokenRefreshSchema),
            ...commonErrorResponses,
        },
    },
    sessionPersist: {
        method: 'POST',
        path: '/auth/session/persist',
        body: c.noBody(),
        responses: {
            200: requiredPayloadEnvelope(tokenRefreshSchema),
            ...commonErrorResponses,
        },
    },
    logout: {
        method: 'POST',
        path: '/auth/logout',
        body: c.noBody(),
        responses: {
            200: emptyEnvelopeSchema,
            ...commonErrorResponses,
        },
    },
    getProviders: {
        method: 'GET',
        path: '/auth/providers',
        responses: {
            200: requiredPayloadEnvelope(z.object({
                providers: z.array(z.object({
                    id: z.string(),
                    startPath: z.string(),
                })),
            })),
            ...commonErrorResponses,
        },
    },
    resetTempPassword: {
        method: 'PATCH',
        path: '/auth/reset-temp',
        body: z.object({
            /** 클라이언트가 서버 enc 공개키로 봉인한 값(JWE Compact). 평문 비밀번호는 계약에 실리지 않는다. */
            currentPassword: secretValueSchema.optional(),
            newPassword: secretValueSchema.optional(),
        }),
        responses: {
            200: requiredPayloadEnvelope(tokenRefreshSchema),
            ...commonErrorResponses,
        },
    },
    setInitialPassword: {
        method: 'PATCH',
        path: '/auth/password/set',
        body: z.object({
            /** 클라이언트가 서버 enc 공개키로 봉인한 값(JWE Compact). 평문 비밀번호는 계약에 실리지 않는다. */
            newPassword: secretValueSchema.optional(),
        }),
        responses: {
            200: emptyEnvelopeSchema,
            ...commonErrorResponses,
        },
    },
    oauthLinkStart: {
        method: 'POST',
        path: '/auth/oauth/:provider/link/start',
        pathParams: z.object({
            provider: z.string().min(1),
        }),
        body: z.object({
            intent: z.enum(['link', 'upgrade-scopes']).optional().default('link'),
        }),
        responses: {
            200: requiredPayloadEnvelope(z.object({ redirectUrl: z.string() })),
            ...commonErrorResponses,
        },
    },
    /** 로그인 상태에서 이메일 등록·변경 — OTP 발송. 이미 본인 활성 이메일이면 alreadyBound. */
    emailBindStart: {
        method: 'POST',
        path: '/auth/email/bind/start',
        body: z.object({
            email: z.string().optional(),
        }),
        responses: {
            200: requiredPayloadEnvelope(verificationTimingSchema.extend({
                alreadyBound: z.boolean().optional(),
            })),
            ...commonErrorResponses,
        },
    },
    /** 로그인 상태에서 이메일 OTP 확인 후 LoginMethod 부착·변경. */
    emailBindVerify: {
        method: 'POST',
        path: '/auth/email/bind/verify',
        body: z.object({
            email: z.string().optional(),
            code: z.string().optional(),
        }),
        responses: {
            200: requiredPayloadEnvelope(z.object({ user: authUserSchema })),
            ...commonErrorResponses,
        },
    },
    oauthCompletePending: {
        method: 'POST',
        path: '/auth/oauth/complete-pending',
        body: z.object({
            acceptances: z.array(termAcceptanceSchema),
            dpopProof: z.string().optional(),
            rememberMe: z.boolean().optional(),
        }),
        responses: {
            200: requiredPayloadEnvelope(authOrMfaResponseSchema),
            ...commonErrorResponses,
        },
    },
    listSessions: {
        method: 'GET',
        path: '/auth/sessions',
        responses: {
            200: requiredPayloadEnvelope(z.object({ sessions: z.array(sessionSchema) })),
            ...commonErrorResponses,
        },
    },
    revokeSession: {
        method: 'POST',
        path: '/auth/sessions/:familyId/revoke',
        pathParams: z.object({ familyId: z.string() }),
        body: c.noBody(),
        responses: {
            200: emptyEnvelopeSchema,
            ...commonErrorResponses,
        },
    },
    listLoginEvents: {
        method: 'GET',
        path: '/auth/login-events',
        query: z.object({
            cursor: z.string().optional(),
        }),
        responses: {
            200: requiredPayloadEnvelope(z.object({ events: z.array(loginEventSchema), nextCursor: z.string().nullable() })),
            ...commonErrorResponses,
        },
    },
});
export { authResponseSchema, authUserSchema, mfaRequiredResponseSchema, authOrMfaResponseSchema };
//# sourceMappingURL=auth.contract.js.map